/**
 * PQP (Post-Quantum Protection) System
 * Core protection mechanism that sweeps ALL funds to fresh quantum-safe addresses
 * This is the PRIMARY protection - preventing address reuse after key exposure
 */

import { QuantumKeyManager, type QuantumKeyPair } from './quantum-keys'
import { pqpHDWallet, type AddressInfo } from './pqp-hd-wallet'
import { sha256 } from '@noble/hashes/sha256'
import { blockchainAPI } from '../api/blockchain-api'

export interface UTXO {
  txid: string
  vout: number
  value: number
  scriptPubKey: string
  address: string
  confirmations: number
}

export interface PQPTransaction {
  type: 'sweep' | 'payment'
  inputs: UTXO[]
  outputs: Array<{
    address: string
    value: number
    type: 'payment' | 'change'
  }>
  protectionData: {
    qd3Data?: string
    quantumCommitment: string
    recoveryData: string
  }
  rbfDisabled: boolean
  fee: number
  totalValue: number
}

export interface PQPProtectionResult {
  transaction: PQPTransaction
  quantumSafeAddresses: string[]
  protectionCommitment: string
  recoveryInstructions: string[]
}

export class PQPProtection {
  private static instance: PQPProtection

  static getInstance(): PQPProtection {
    if (!PQPProtection.instance) {
      PQPProtection.instance = new PQPProtection()
    }
    return PQPProtection.instance
  }

  /**
   * Create PQP-protected transaction that sweeps ALL funds to quantum-safe addresses
   * This is the core protection mechanism
   */
  async createPQPTransaction(
    fromAddress: string,
    recipientAddress: string,
    paymentAmount: number,
    utxos: UTXO[],
    feeRate: number = 10 // sats per vbyte
  ): Promise<PQPProtectionResult> {
    // Step 1: Validate inputs
    if (paymentAmount <= 0) {
      throw new Error('Payment amount must be positive')
    }

    const totalInputValue = utxos.reduce((sum, utxo) => sum + utxo.value, 0)
    if (totalInputValue < paymentAmount) {
      throw new Error('Insufficient funds')
    }

    // Step 2: Generate fresh quantum-safe addresses for change
    const changeAddress = await pqpHDWallet.getNextAddress(0, true, false) // Change address
    
    // Step 3: Calculate fees and outputs
    const estimatedFee = this.estimateTransactionFee(utxos.length, 2, feeRate) // 2 outputs typically
    const changeAmount = totalInputValue - paymentAmount - estimatedFee

    if (changeAmount < 0) {
      throw new Error('Insufficient funds to cover payment and fees')
    }

    // Step 4: Create transaction outputs
    const outputs: Array<{
      address: string
      value: number
      type: 'payment' | 'change'
    }> = [
      {
        address: recipientAddress,
        value: paymentAmount,
        type: 'payment'
      }
    ]

    // Only add change output if amount is above dust threshold
    const dustThreshold = 546 // Standard Bitcoin dust threshold
    if (changeAmount > dustThreshold) {
      outputs.push({
        address: changeAddress.address,
        value: changeAmount,
        type: 'change'
      })
    }

    // Step 5: Create quantum protection commitment
    const protectionCommitment = await this.createQuantumCommitment(
      fromAddress,
      changeAddress.address,
      paymentAmount,
      Date.now()
    )

    // Step 6: Generate QD3 attestation data
    const qd3Data = await this.createQD3Attestation(fromAddress, changeAddress.address)

    // Step 7: Create recovery data
    const recoveryData = await this.createRecoveryData(fromAddress, changeAddress.address, totalInputValue)

    // Step 8: Build PQP transaction
    const pqpTransaction: PQPTransaction = {
      type: 'sweep',
      inputs: utxos,
      outputs,
      protectionData: {
        qd3Data,
        quantumCommitment: protectionCommitment,
        recoveryData
      },
      rbfDisabled: true, // Critical: prevent reversion to exposed address
      fee: estimatedFee,
      totalValue: totalInputValue
    }

    // Step 9: Mark source address as quantum-compromised
    pqpHDWallet.markAddressUsed(fromAddress)

    return {
      transaction: pqpTransaction,
      quantumSafeAddresses: [changeAddress.address],
      protectionCommitment,
      recoveryInstructions: [
        'Funds have been swept to quantum-safe addresses',
        'Source address is now considered quantum-compromised',
        'Change address uses fresh quantum keys',
        'Recovery paths available if transaction fails'
      ]
    }
  }

  /**
   * Create quantum protection commitment
   */
  private async createQuantumCommitment(
    sourceAddress: string,
    changeAddress: string,
    amount: number,
    timestamp: number
  ): Promise<string> {
    const commitmentData = {
      version: 1,
      sourceAddress,
      changeAddress,
      amount,
      timestamp,
      protection: 'PQP_v1'
    }

    const dataString = JSON.stringify(commitmentData)
    const commitment = sha256(new TextEncoder().encode(dataString))
    
    return Array.from(commitment)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  }

  /**
   * Create QD3 attestation data for OP_RETURN
   */
  private async createQD3Attestation(
    sourceAddress: string,
    changeAddress: string
  ): Promise<string | undefined> {
    try {
      // Get quantum keys for change address
      const keys = await pqpHDWallet.getKeysForAddress(changeAddress)
      if (!keys) {
        console.warn('Could not get quantum keys for change address')
        return undefined
      }

      // Create attestation message
      const message = `PQP protection: ${sourceAddress} -> ${changeAddress} at ${new Date().toISOString()}`
      const messageBytes = new TextEncoder().encode(message)

      // Generate quantum signature
      const signature = await QuantumKeyManager.signMessage(
        keys.quantum.keyPair.privateKey,
        messageBytes
      )

      // Create QD3 data
      const qd3Data = await QuantumKeyManager.createQD3Data(
        keys.quantum.keyPair.publicKey,
        signature
      )

      return qd3Data.hex
    } catch (error) {
      console.error('Failed to create QD3 attestation:', error)
      return undefined
    }
  }

  /**
   * Create recovery data for failed transactions
   */
  private async createRecoveryData(
    sourceAddress: string,
    safeAddress: string,
    totalValue: number
  ): Promise<string> {
    const recoveryInfo = {
      version: 1,
      sourceAddress,
      safeAddress,
      totalValue,
      recoveryMethods: [
        'CPFP (Child Pays For Parent)',
        'Time-locked sweep to safe address',
        'Emergency quantum key reveal'
      ],
      createdAt: Date.now()
    }

    const recoveryDataBytes = new TextEncoder().encode(JSON.stringify(recoveryInfo))
    const hash = sha256(recoveryDataBytes)
    
    return Array.from(hash)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  }

  /**
   * Estimate transaction fee based on inputs/outputs
   */
  private estimateTransactionFee(
    inputCount: number,
    outputCount: number,
    feeRate: number
  ): number {
    // Rough estimation for P2WPKH inputs and outputs
    const inputSize = 148 // bytes per input (P2WPKH)
    const outputSize = 34 // bytes per output (P2WPKH)
    const baseSize = 10 // transaction overhead
    
    const estimatedSize = baseSize + (inputCount * inputSize) + (outputCount * outputSize)
    return Math.ceil(estimatedSize * feeRate)
  }

  /**
   * Validate if address needs quantum protection
   */
  async needsQuantumProtection(address: string): Promise<{
    needsProtection: boolean
    reason: string
    risk: 'low' | 'medium' | 'high' | 'critical' | 'protected'
    hasExposedPublicKey?: boolean
    hasQD3Registration?: boolean
    qd3TxId?: string
    /**
     * False when the registration scan could not read the address's full
     * history. Everything below about QD3 status is then "not found so far",
     * not "not registered" — see BlockchainAPI.checkQD3Registration.
     */
    qd3ScanComplete?: boolean
  }> {
    // First check for QD3 registration
    const qd3Status = await blockchainAPI.checkQD3Registration(address)
    const qd3ScanComplete = qd3Status.scanComplete !== false

    if (qd3Status.isRegistered) {
      return {
        needsProtection: false,
        reason: `Address has QD3 quantum key registration (TX: ${qd3Status.registrationTxId?.slice(0, 8)}...)`,
        risk: 'protected',
        hasQD3Registration: true,
        qd3TxId: qd3Status.registrationTxId,
        qd3ScanComplete
      }
    }
    
    // Check if address has been used (public key revealed)
    const addressInfo = await this.getAddressInfo(address)
    
    // If address has spent funds, public key is exposed
    if (addressInfo.hasSpent) {
      return {
        needsProtection: true,
        reason: 'Address has spent funds - public key is exposed',
        risk: 'critical',
        hasExposedPublicKey: true,
        hasQD3Registration: false,
        qd3ScanComplete
      }
    }

    // If address has balance but never spent
    if (addressInfo.balance > 0) {
      return {
        needsProtection: true,
        reason: 'Address has funds but no spending history - preventive protection recommended',
        risk: 'medium',
        hasExposedPublicKey: false,
        hasQD3Registration: false,
        qd3ScanComplete
      }
    }

    // If address has received funds before (even if spent)
    if (addressInfo.hasTransactions) {
      return {
        needsProtection: false,
        reason: 'Address has transaction history but no current funds',
        risk: 'low',
        hasExposedPublicKey: addressInfo.hasSpent,
        hasQD3Registration: false,
        qd3ScanComplete
      }
    }

    return {
      needsProtection: false,
      reason: 'Address has no funds or transaction history',
      risk: 'low',
      hasExposedPublicKey: false,
      hasQD3Registration: false
    }
  }

  /**
   * Get address information for risk assessment
   */
  private async getAddressInfo(address: string): Promise<{
    hasTransactions: boolean
    balance: number
    txCount: number
    hasSpent: boolean
  }> {
    try {
      const info = await blockchainAPI.getAddressInfo(address)
      return {
        hasTransactions: info.hasTransactions,
        balance: info.balance,
        txCount: info.txCount,
        hasSpent: info.hasSpent
      }
    } catch (error) {
      console.error('Error fetching address info:', error)
      // Return safe defaults on error
      return {
        hasTransactions: false,
        balance: 0,
        txCount: 0,
        hasSpent: false
      }
    }
  }

  /**
   * Monitor quantum threat level
   */
  async getQuantumThreatLevel(): Promise<{
    level: 'minimal' | 'emerging' | 'imminent' | 'active'
    description: string
    recommendation: string
  }> {
    // This would monitor quantum computing developments
    // For now, return current assessment
    return {
      level: 'emerging',
      description: 'Quantum computers are advancing but not yet capable of breaking Bitcoin',
      recommendation: 'Implement PQP protection proactively for high-value addresses'
    }
  }

  /**
   * Create emergency sweep transaction for immediate protection
   */
  async createEmergencySweep(
    addresses: string[],
    emergencyAddress: string
  ): Promise<PQPTransaction[]> {
    const transactions: PQPTransaction[] = []

    for (const address of addresses) {
      // Get all UTXOs for address
      const utxos = await this.getUTXOsForAddress(address)
      
      if (utxos.length === 0) continue

      const totalValue = utxos.reduce((sum, utxo) => sum + utxo.value, 0)
      const fee = this.estimateTransactionFee(utxos.length, 1, 20) // Higher fee for priority

      if (totalValue <= fee) continue // Skip if insufficient funds

      const emergencyTx: PQPTransaction = {
        type: 'sweep',
        inputs: utxos,
        outputs: [{
          address: emergencyAddress,
          value: totalValue - fee,
          type: 'change'
        }],
        protectionData: {
          quantumCommitment: await this.createQuantumCommitment(address, emergencyAddress, totalValue, Date.now()),
          recoveryData: await this.createRecoveryData(address, emergencyAddress, totalValue)
        },
        rbfDisabled: true,
        fee,
        totalValue
      }

      transactions.push(emergencyTx)
    }

    return transactions
  }

  /**
   * Get UTXOs for address
   */
  private async getUTXOsForAddress(address: string): Promise<UTXO[]> {
    try {
      const utxos = await blockchainAPI.getUTXOs(address)
      // Map to our UTXO format
      return utxos.map(utxo => ({
        txid: utxo.txid,
        vout: utxo.vout,
        value: utxo.value,
        scriptPubKey: '', // Would need to fetch from transaction
        address: address,
        confirmations: utxo.status.confirmed ? 1 : 0 // Simplified for now
      }))
    } catch (error) {
      console.error('Error fetching UTXOs:', error)
      return []
    }
  }
}

// Export singleton instance
export const pqpProtection = PQPProtection.getInstance()